import { PrinterProfile } from '../../types/printerProfile';
import {
  PortedTicketData as TicketData,
  PortedCartItem as CartItem,
  PortedTaxResult as TaxResult,
  PortedCustomizationOption as CustomizationOption,
} from './types';
import { groupItemsByCategory, aggregateCustomizationOptions } from './grouping';
import { calculateTax, getTaxDisplayFlags } from './tax';
import { tk, tkOrderTypeUpper, tkPayment } from './i18nShim';
import { catStart, catEnd } from './categoryMarker';
import { formatDateTime, currentLocale } from './dateFormat';

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * MODULE DE TICKET UNIFIÉ — un seul rendu, régulier sur TOUT le parc d'imprimantes
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Cible : `window.android.print(contenu)` de l'app RConsol v5.8. Le même texte est
 * ensuite traité par QUATRE pilotes natifs très différents :
 *
 *   • ESC/POS (chinoises génériques, Epson TM-T20) → `MainActivity.getAsyncEscPosPrinter()`
 *     puis la bibliothèque DantSu (`escposprinter/…/textparser/`) ;
 *   • Star USB  → `MainActivity.continuePrintWithStarInternal()` : boucle ligne par
 *     ligne, `builder.append(cleanLine.getBytes("Windows-1252"))` — TEXTE NATIF ;
 *   • Star Bluetooth → `MainActivity.createBitmapFromText()` : le texte est DESSINÉ
 *     par Android sur un Canvas en `Typeface.MONOSPACE` ;
 *   • Sunmi → le texte part tel quel, aucune balise n'est interprétée.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * POURQUOI DU TEXTE ET PAS UNE IMAGE (technique Uber Eats / Deliveroo)
 * ───────────────────────────────────────────────────────────────────────────────
 * L'image raster donnerait un rendu strictement identique partout, MAIS elle est
 * impossible avec le pont actuel :
 *   • aucune méthode `printImage/printBitmap/printBase64/printRaw` parmi les
 *     @JavascriptInterface exposées par MainActivity (aucun `Base64.decode` dans l'app) ;
 *   • la seule voie indirecte serait la balise `<img>HEX</img>` de DantSu
 *     (`PrinterTextParserImg.java` → `EscPosPrinterCommands` `GS v 0`), qui n'existe
 *     QUE sur le chemin ESC/POS ;
 *   • sur Star USB la ligne est envoyée en texte brut : une balise `<img>` sortirait
 *     en HEXADÉCIMAL IMPRIMÉ EN CLAIR sur des mètres de papier. Sur Star BT elle serait
 *     DESSINÉE comme du texte. Le parc contient des Star TSP100 → option éliminée.
 * ⇒ Le rendu régulier s'obtient donc par du TEXTE À CHASSE FIXE dont TOUT le
 *    remplissage est calculé ici, en JS, en espaces. C'est le seul dénominateur
 *    commun aux quatre pilotes.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * LE CONTRAT DUR (chaque règle est adossée à une ligne de code natif)
 * ───────────────────────────────────────────────────────────────────────────────
 * R1  `\n` UNIQUEMENT, jamais `\r\n` : les pilotes font `text.split("\n")` ; le `\r`
 *     survit, compte pour une colonne et part à l'imprimante.
 * R2  ZÉRO octet ESC/POS brut (`\x1B…`, `\x1D…`) :
 *      – `PrinterTextParserString` mesure `getBytes().length` → remplissage faussé ;
 *      – `PrinterTextParserLine` crée une colonne fantôme si un octet précède une balise ;
 *      – `EscPosPrinter` réémet de toute façon `ESC @` puis `ESC t 16` avant chaque
 *        fragment : tout registre posé à la main est écrasé ;
 *      – sur Star BT les octets sont DESSINÉS comme des glyphes.
 * R3  UNE SEULE balise d'alignement par ligne, `[L]` ou `[C]`. JAMAIS `[L]…[R]…`
 *     (voir le bloc « padPair » plus bas : c'est la cause du bug de production).
 * R4  JAMAIS une ligne valant exactement `ARTICLES` : `getAsyncEscPosPrinter()` arme
 *     alors `inArticlesSection`, qui DÉTRUIT les prix des lignes contenant `x<chiffre>`
 *     (`Pizza x2[R]18,00` → `Pizza x21800`). Sans cette ligne, tout le bloc est inerte.
 * R5  AUCUNE ligne commençant (balises retirées + trim) par `customer :`, `notes:` ou
 *     `note:` : le natif injecte alors une ligne `[C]------------------------` parasite.
 * R6  JAMAIS 24 tirets consécutifs ou plus, NULLE PART : dès que le tampon natif
 *     contient `------------------------`, deux lignes identiques consécutives font
 *     DISPARAÎTRE la seconde (un article commandé 2× de suite s'évapore) et un
 *     séparateur parasite est ajouté en fin de ticket. On sépare donc au `=`.
 * R7  AUCUNE coupe, AUCUNE ligne d'avance finale : la coupe est INCONDITIONNELLE côté
 *     natif (ESC/POS `printFormattedTextAndCut` précédé de 20 mm d'avance ; Star
 *     `append("\n\n\n\n\n") + appendCutPaper`). En ajouter = papier gâché.
 * R8  AUCUN caractère hors CP1252 : `getBytes("windows-1252")` transforme le reste en
 *     `?` ET fausse le remplissage (un emoji = 2 unités UTF-16 en JS, 1 octet imprimé).
 * R9  PAS de `<font size='…'>` sur une ligne qui porte du remplissage : la longueur y
 *     est multipliée par 2 à 7 → la ligne déborde.
 * R10 Attributs de balises en GUILLEMETS SIMPLES (`PrinterTextParserTag` cherche `='`).
 * R11 Une balise inconnue (`<i>`, `<br>`, `<span>`) s'IMPRIME EN CLAIR.
 * R12 Un `<barcode>`/`<qrcode>` mal formé fait perdre le ticket ENTIER + boîte de
 *     dialogue bloquante → ce module n'en émet aucun.
 * R13 Au plus UNE ligne vide consécutive (`printQueue.stripMarkers()` applique
 *     `replace(/\n{3,}/g,'\n\n')`).
 *
 * Balises réellement utilisables : `[L]`, `[C]`, `[R]`, `<b>`, `<u>`, `<font…>`. Rien d'autre.
 */

/* ═══════════════════════════════════════════════════════════════════════════════
 * MODÈLE DE LIGNES
 * On ne concatène pas des chaînes : on construit un tableau de lignes TYPÉES, puis on
 * le rend. C'est ce qui permet de garantir la règle « toute ligne à deux colonnes fait
 * EXACTEMENT W caractères » sans avoir à y penser à chaque endroit.
 * ═══════════════════════════════════════════════════════════════════════════════ */
type Ln =
  | { k: 'blank' }
  | { k: 'sep' }
  | { k: 'c'; t: string; b?: boolean; big?: boolean }
  | { k: 'l'; t: string; b?: boolean }
  | { k: 'pair'; left: string; right: string; b?: boolean }
  | { k: 'raw'; t: string };

export interface UnifiedOpts {
  /** Largeur de composition en caractères. Voir le bloc « LARGEUR » ci-dessous. */
  width?: number;
  /** `'plain'` = aucune balise (Sunmi) : le centrage passe côté JS. */
  markup?: 'escpos' | 'plain';
  /** Suffixe monétaire. `'EUR'` par défaut : le glyphe `€` dépend de la page de code. */
  currency?: string;
  /** Émettre les balises `[CAT:…]` de routage cuisine (défaut : oui). */
  emitCategoryMarkers?: boolean;
  /** Retirer les accents (clones chinois qui n'implémentent pas la page de code 16). */
  asciiOnly?: boolean;
}

/* ═══════════════════════════════════════════════════════════════════════════════
 * LARGEUR (W)
 * ═══════════════════════════════════════════════════════════════════════════════
 * ⚠️ La console NE PEUT PAS connaître la largeur réelle. Côté natif :
 *     new AsyncEscPosPrinter(conn, 203, paperWidth, paperWidth == 48f ? 32 : 48)
 * `paperWidth` est un champ PRIVÉ, lu de SharedPreferences("PrinterPrefs") et modifiable
 * uniquement par le menu Android « Sélectionner Largeur ». Aucun @JavascriptInterface
 * ne l'expose.
 *
 * Conséquence : W = 32 par défaut. Une ligne padée à 32 s'imprime IDENTIQUEMENT sur une
 * imprimante 32 ET 48 colonnes (le parseur DantSu ajoute `nbrCaracteres - W` espaces À
 * DROITE, jamais de reflux). En 80 mm le corps occupe les 2/3 gauche mais reste
 * parfaitement aligné. W = 48 seulement une fois confirmé que TOUS les Android du parc
 * sont réglés sur 80 mm : W trop grand ⇒ retour chariot matériel ⇒ ticket cassé.
 */
const DEFAULT_WIDTH = 32;

/* ═══════════════════════════════════════════════════════════════════════════════
 * ASSAINISSEMENT CP1252 (R8)
 * ═══════════════════════════════════════════════════════════════════════════════ */

/**
 * Remplacements EXPLICITES des caractères « typographiques » qui, bien que présents
 * dans CP1252 pour certains, ne sont pas rendus par une bonne partie des clones
 * chinois (et faussent la lecture quand ils sortent en `?`).
 * Les espaces insécables/fines viennent de `toLocaleString()` en français : sans ce
 * remplacement, elles comptent pour une colonne mais s'impriment en `?`.
 */
const CHAR_FIXES: Array<[RegExp, string]> = [
  // Espaces exotiques (insecable, fine, demi-cadratin...) -> espace simple.
  [/[   -​  　﻿]/g, ' '],
  [/[‐‑‒–—―−]/g, '-'],
  [/[‘’‚′´]/g, "'"],
  [/[“”„″«»]/g, '"'],
  [/[…]/g, '...'],
  [/[•·●▪■]/g, '*'],
  [/[→⇒›]/g, '>'],
  [/[←⇐‹]/g, '<'],
  [/[✓✔]/g, 'v'],
  [/[€]/g, 'EUR'],
  [/[œ]/g, 'oe'],
  [/[Œ]/g, 'OE'],
  [/[™]/g, '(TM)'],
  [/[©]/g, '(C)'],
  [/[®]/g, '(R)'],
];

/** Diacritiques combinatoires (U+0300–U+036F), retirés lors du repli par décomposition. */
const COMBINING = /[̀-ͯ]/g;

/** Jeu retenu : ASCII imprimable + Latin-1 haut (accents français, `°`, `§`…). */
const CP1252_SAFE = /^[\x20-\x7E¡-ÿ]$/;

/**
 * Rend une chaîne SÛRE pour `getBytes("windows-1252")` :
 * 1. suppression des emoji/symboles (chaque paire de substitution UTF-16 fausserait
 *    le remplissage de DEUX colonnes pour UN seul octet imprimé) ;
 * 2. repli par décomposition Unicode (`ā` → `a`) avant le `?` de dernier recours ;
 * 3. neutralisation des séries de tirets (R6) : plus jamais 24 tirets d'affilée, même
 *    si un nom de produit ou une note client en contient ;
 * 4. suppression des `[` `]` `<` `>` parasites qui feraient croire à une balise
 *    (R11 : une balise inconnue s'imprime en clair, et `[C]` au milieu d'un nom de
 *    produit déclencherait un changement d'alignement sur Star).
 */
const sanitize = (input: unknown, asciiOnly = false): string => {
  let s = String(input ?? '').normalize('NFC');
  for (const [re, rep] of CHAR_FIXES) s = s.replace(re, rep);
  // R1 : aucun retour chariot ne doit survivre (il compterait pour une colonne).
  s = s.replace(/[\r\n\t\v\f]/g, ' ');
  // R2 : aucun octet de contrôle (un ESC glissé depuis une note client casserait tout).
  s = s.replace(/[\x00-\x1F\x7F]/g, '');

  let out = '';
  for (const ch of s) {
    if (CP1252_SAFE.test(ch)) {
      out += ch;
      continue;
    }
    const folded = ch.normalize('NFD').replace(COMBINING, '');
    out += folded.length === 1 && CP1252_SAFE.test(folded) ? folded : '';
  }

  if (asciiOnly) {
    out = out.normalize('NFD').replace(COMBINING, '');
    out = out.replace(/[^\x20-\x7E]/g, '');
  }

  // R6 + R11 : séries de tirets bornées, chevrons et crochets neutralisés.
  out = out.replace(/-{4,}/g, '---');
  out = out.replace(/[<>[\]]/g, ' ');

  return out.replace(/\s+/g, ' ').trim();
};

/* ═══════════════════════════════════════════════════════════════════════════════
 * MISE EN FORME NUMÉRIQUE
 * ═══════════════════════════════════════════════════════════════════════════════ */

/** Séparateur décimal de la langue d'interface (`,` en fr, `.` en en). */
const decimalSep = (): string => {
  try {
    const parts = (1.1).toLocaleString(currentLocale());
    return parts.includes(',') ? ',' : '.';
  } catch {
    return ',';
  }
};

/**
 * Montant SANS groupement de milliers (`toFixed`), pour que la longueur soit
 * prévisible et qu'aucune espace insécable de groupement ne s'invite (R8).
 */
const money = (v: unknown): string => {
  const n = Number(v);
  return (Number.isFinite(n) ? n : 0).toFixed(2).replace('.', decimalSep());
};

const round2 = (v: unknown): number => Math.round((Number(v) || 0) * 100) / 100;

/* ═══════════════════════════════════════════════════════════════════════════════
 * PIÈGES DU PRÉ-TRAITEMENT NATIF (R4 / R5)
 * ═══════════════════════════════════════════════════════════════════════════════ */

/** Préfixes qui déclenchent l'injection d'un séparateur parasite côté natif (R5). */
const COMMENT_PREFIXES = ['customer :', 'notes:', 'note:'];

/**
 * Empêche une ligne de ressembler à un « commentaire » pour le natif.
 * On préfixe d'une étoile plutôt que d'un espace : le natif fait `trim()` avant le test,
 * un espace ne protégerait de rien.
 */
const guardComment = (text: string): string => {
  const probe = text.trim().toLowerCase();
  return COMMENT_PREFIXES.some((p) => probe.startsWith(p)) ? `* ${text}` : text;
};

/**
 * Empêche une ligne de valoir exactement `ARTICLES` (R4).
 *
 * Le natif teste la ligne BRUTE (`line.trim().equalsIgnoreCase("ARTICLES")`, balises
 * comprises), donc `[C]<b>ARTICLES</b>` est déjà hors de portée — mais en mode `plain`
 * les balises disparaissent et une catégorie qui s'appellerait « Articles » armerait
 * `inArticlesSection`, ce qui DÉTRUIRAIT ensuite les prix. Ajouter des espaces ne
 * servirait à rien (le natif fait `trim()` avant le test) : il faut un caractère visible.
 */
const guardArticles = (line: string): string =>
  line.trim().toUpperCase() === 'ARTICLES' ? `${line} *` : line;

/* ═══════════════════════════════════════════════════════════════════════════════
 * COMPOSITION À CHASSE FIXE — le cœur du module
 * ═══════════════════════════════════════════════════════════════════════════════ */

/**
 * Ligne à DEUX COLONNES, longueur TOUJOURS exactement W.
 *
 * ⚠️ POURQUOI PAS `[L]libellé[R]montant` — c'est LE bug de production :
 *   • Star USB : `line.replace("[C]","").replace("[L]","").replace("[R]","")` →
 *     `[L]Pizza[R]12,00` devient `Pizza12,00`, aligné à droite. Montant collé.
 *   • Star BT : même suppression puis `trim()` + rendu Canvas → même collage.
 *   • ESC/POS : le pré-traitement reconstruit la ligne en `[L]<nom> x<qté>` dès que
 *     `inArticlesSection` est armé — LE PRIX EST DÉTRUIT et la quantité corrompue.
 *   • Sunmi : les balises s'impriment littéralement.
 *
 * ⚠️ POURQUOI LE REMPLISSAGE JS, LUI, MARCHE PARTOUT :
 *   • ESC/POS : `PrinterTextParserColumn` ne retire que le préfixe de 3 caractères ; les
 *     espaces INTÉRIEURS sont consommés bruts, puis `nbrCaracteres - W` espaces sont
 *     ajoutés À DROITE. Padder à W ≤ largeur native est donc sans effet destructeur.
 *   • Star USB : `cleanLine` n'est pas trimé, fonte à chasse fixe → padding au caractère.
 *   • Star BT : `trim()` ne mange que la tête et la queue, jamais l'intérieur ; fonte
 *     `Typeface.MONOSPACE`. ⚠️ COROLLAIRE : `createBitmapFromText` rétrécit CHAQUE LIGNE
 *     INDÉPENDAMMENT si elle dépasse (`scale = width*0.95/textWidth`). Pour que les
 *     colonnes restent alignées ENTRE ELLES, toutes les lignes doivent tenir dans W —
 *     d'où la troncature ci-dessous, jamais un débordement « toléré ».
 */
const padPair = (left: string, right: string, w: number): string => {
  const r = right.slice(0, Math.max(0, w));
  const maxLeft = Math.max(0, w - r.length - (r.length ? 1 : 0));
  const l = left.length > maxLeft ? left.slice(0, maxLeft) : left;
  const gap = Math.max(r.length ? 1 : 0, w - l.length - r.length);
  return (l + ' '.repeat(gap) + r).slice(0, w);
};

/** Centrage calculé en JS (mode `plain`, où `[C]` n'existe pas). */
const padCenter = (text: string, w: number): string => {
  const t = text.slice(0, w);
  const left = Math.max(0, Math.floor((w - t.length) / 2));
  return ' '.repeat(left) + t;
};

/**
 * Retour à la ligne LOGICIEL.
 * ⚠️ Aucun des quatre pilotes n'a de word-wrap : une ligne trop longue est soit coupée
 * au caractère par l'imprimante (ESC/POS, Star USB), soit RÉTRÉCIE (Star BT), ce qui
 * désaligne toute la colonne des prix. On coupe donc nous-mêmes, aux espaces.
 */
const wrap = (text: string, maxLen: number): string[] => {
  const limit = Math.max(4, maxLen);
  const words = text.split(' ').filter(Boolean);
  const out: string[] = [];
  let cur = '';
  for (const word of words) {
    const piece = word.length > limit ? word.slice(0, limit) : word;
    if (!cur) {
      cur = piece;
    } else if (cur.length + 1 + piece.length <= limit) {
      cur += ` ${piece}`;
    } else {
      out.push(cur);
      cur = piece;
    }
  }
  if (cur) out.push(cur);
  return out.length ? out : [''];
};

/* ═══════════════════════════════════════════════════════════════════════════════
 * CONSTRUCTION DU TICKET
 * ═══════════════════════════════════════════════════════════════════════════════ */

export function buildUnifiedTicket(data: TicketData, opts: UnifiedOpts = {}): string {
  const w = Math.max(24, Math.min(64, Math.floor(opts.width || DEFAULT_WIDTH)));
  const markup = opts.markup === 'plain' ? 'plain' : 'escpos';
  const ascii = opts.asciiOnly === true;
  const cur = sanitize(opts.currency ?? 'EUR', ascii) || 'EUR';
  const withMarkers = opts.emitCategoryMarkers !== false;

  const L: Ln[] = [];
  const S = (v: unknown) => sanitize(v, ascii);

  const blank = () => L.push({ k: 'blank' });
  const sep = () => L.push({ k: 'sep' });
  const cen = (t: string, o?: { b?: boolean; big?: boolean }) =>
    t && L.push({ k: 'c', t, b: o?.b, big: o?.big });
  const left = (t: string, b?: boolean) => t && L.push({ k: 'l', t, b });
  const pair = (l: string, r: string, b?: boolean) => L.push({ k: 'pair', left: l, right: r, b });

  /** Libellé + montant, avec repli sur plusieurs lignes quand le libellé est trop long. */
  const priced = (label: string, amount: string, bold?: boolean) => {
    const budget = w - amount.length - 1;
    if (label.length <= budget) {
      pair(label, amount, bold);
      return;
    }
    const parts = wrap(label, budget);
    for (let i = 0; i < parts.length - 1; i += 1) left(parts[i], bold);
    pair(parts[parts.length - 1], amount, bold);
  };

  /* ── 1. En-tête ÉTABLISSEMENT ─────────────────────────────────────────────── */
  const storeName = S(data.storeInfo?.name).toUpperCase();
  cen(storeName || S(tk('header.defaultRestaurant')).toUpperCase(), { b: true });
  cen(S(data.storeInfo?.address));
  if (data.storeInfo?.phone) cen(`${S(tk('header.phoneShort'))} ${S(data.storeInfo.phone)}`);
  sep();

  /* ── 2. En-tête TRANSACTION ───────────────────────────────────────────────── */
  // Numéro : 2 derniers chiffres (repérage rapide au comptoir) sauf demande explicite
  // du code complet (commandes web, pour rapprochement avec le site).
  const rawCode = S(data.orderCode ?? '');
  const shortCode = rawCode && rawCode !== 'N/A' ? rawCode.slice(-2).padStart(2, '0') : rawCode;
  const code = (data.printFullOrderCode ? rawCode : shortCode) || '--';
  cen(`${S(tk('header.orderUpper'))} #${code}`, { b: true });

  const typeLine = [
    S(tkOrderTypeUpper(data.orderType)),
    data.tableNumber ? `${S(tk('header.tableUpper'))} ${S(data.tableNumber)}` : '',
  ]
    .filter(Boolean)
    .join(' - ');
  cen(typeLine, { b: true });

  cen(S(formatDateTime(data.orderDate)));

  const seller = [data.seller_code, data.created_by_name]
    .filter((v) => !!v && String(v).trim() !== '')
    .map((v) => S(v))
    .join(' - ');
  if (seller) cen(`${S(tk('header.seller'))} ${seller}`);

  // Client : jamais préfixé `Customer :` (R5) — le libellé traduit est « Client ».
  if (data.customerName) cen(`${S(tk('header.client'))} : ${S(data.customerName)}`);
  if (data.customerPhone) cen(`${S(tk('header.phoneShort'))} ${S(data.customerPhone)}`);
  if (data.customerAddress) {
    for (const part of wrap(`${S(tk('header.address'))} : ${S(data.customerAddress)}`, w)) {
      left(part);
    }
  }
  if (data.confirmationCode !== undefined && data.confirmationCode !== null) {
    cen(`${S(tk('header.clientNumber'))} ${S(data.confirmationCode)}`);
  }
  sep();

  /* ── 3. ARTICLES groupés par CATÉGORIE ────────────────────────────────────── */
  // En-tête de colonnes : `pair`, donc jamais une ligne valant exactement `ARTICLES` (R4).
  pair(S(tk('items.sectionTitle')), S(tk('items.amountUpper')), true);
  blank();

  const items = Array.isArray(data.items) ? data.items : [];
  const groups = groupItemsByCategory(items, data.categories || []);

  const unitPriceOf = (item: CartItem): number => {
    // Le prix des options est DÉJÀ facturé sur sa propre ligne : on ne le réinjecte pas
    // dans le prix de l'article, sinon le total du ticket diverge d'`invoiceTotal`.
    return Number(item.menuItem?.price) || 0;
  };

  const pushOptions = (item: CartItem) => {
    const buckets = item.customizations || {};
    const flat: CustomizationOption[] = [];
    for (const key of Object.keys(buckets)) {
      const arr = buckets[key];
      if (Array.isArray(arr)) flat.push(...arr);
    }
    if (!flat.length) return;
    // Agrégation via le helper PARTAGÉ du projet (pas de logique maison ici).
    for (const opt of aggregateCustomizationOptions(flat)) {
      const qty = opt.quantity > 1 ? `${opt.quantity} ` : '';
      const label = `  + ${qty}${S(opt.name)}`;
      const price = Number(opt.price) || 0;
      if (price > 0) {
        priced(label, `${money(price * opt.quantity * (Number(item.quantity) || 1))}`);
      } else {
        left(label);
      }
    }
  };

  const pushNote = (item: CartItem) => {
    if (!item.note || !String(item.note).trim()) return;
    // « Note : » (espace AVANT le deux-points) et non « Note: » — sinon le natif détecte
    // un commentaire et injecte une ligne de 24 tirets (R5 + R6).
    const text = `  ${S(tk('items.note'))} : ${S(item.note)}`;
    for (const part of wrap(text, w)) left(guardComment(part));
  };

  let printedAny = false;
  groups.forEach((group, index) => {
    if (!group.items.length) return;
    printedAny = true;

    // Titre de catégorie HORS du bloc [CAT:…] : `printQueue.buildCategoryTicket()`
    // reconstruit déjà son propre en-tête `*** CATÉGORIE ***` pour le ticket cuisine ;
    // l'inclure ici produirait un doublon sur le papier de la cuisine.
    cen(S(group.categoryName).toUpperCase(), { b: true });

    // Balises de ROUTAGE — générées EXCLUSIVEMENT par les helpers partagés, qui
    // échappent `[ ] | :`. Un `]` non échappé dans un nom de catégorie ferait échouer à
    // la fois la regex de découpe ET `stripMarkers()` → la balise brute s'imprimerait
    // sur le ticket client. L'id doit rester STRICTEMENT identique entre START et END
    // (backréférence `\1` dans la regex de `printQueue`).
    if (withMarkers) L.push({ k: 'raw', t: catStart(group.categoryId, group.categoryName, index) });

    for (const item of group.items) {
      const qty = Number(item.quantity) || 1;
      // Forme « 2 x Nom » : le `x` n'est JAMAIS suivi d'un chiffre, donc même si le piège
      // `inArticlesSection` était armé, la ligne ne matcherait pas `.*x\d+.*` (R4,
      // défense en profondeur).
      const label = `${qty} x ${S(item.menuItem?.name)}`;
      priced(label, money(unitPriceOf(item) * qty));
      pushOptions(item);
      pushNote(item);
    }

    if (withMarkers) L.push({ k: 'raw', t: catEnd(group.categoryId, group.categoryName, index) });
    blank();
  });

  if (!printedAny) cen(S(tk('items.none')));

  /* ── 4. TOTAUX / TVA ──────────────────────────────────────────────────────── */
  // ⚠️ On ne recalcule JAMAIS le HT à partir du TTC quand l'API le fournit, et on ne
  // re-somme jamais les lignes pour obtenir le total : la somme des lignes ≠
  // `invoiceTotal` (remise fidélité, livraison, arrondis) et un écart d'un centime rend
  // le ticket non conforme NF525.
  const tr: TaxResult | undefined =
    data.taxResult && !Array.isArray(data.taxResult) ? (data.taxResult as TaxResult) : undefined;
  const rateLines = Array.isArray(data.taxResult)
    ? (data.taxResult as Array<{ rate: number; amount: number }>)
    : [];
  const totals = data.orderTotals;

  const totalTTC = round2(tr?.totalTTC ?? totals?.invoiceTotal ?? data.total ?? 0);
  const loyaltyDiscount = round2(totals?.loyaltyDiscount || 0);
  const loyaltyPoints = Number(totals?.loyaltyPointsUsed) || 0;
  // HT et TVA portent sur la base AVANT remise fidélité (cohérent avec les lignes
  // d'articles imprimées) ; le TOTAL reste le montant réellement dû.
  const baseTTC = loyaltyDiscount > 0 ? round2(totalTTC + loyaltyDiscount) : totalTTC;

  const apiRate = tr?.taxRate ?? null;
  const hasHT = tr?.subtotalHT !== undefined && tr?.subtotalHT !== null;
  const hasTax = tr?.taxAmount !== undefined && tr?.taxAmount !== null;
  // Repli par le helper PARTAGÉ `taxDisplayHelper` — aucune arithmétique de TVA maison.
  const fb = hasHT && hasTax && apiRate !== null ? null : calculateTax(baseTTC, apiRate);
  const taxRate = apiRate !== null ? apiRate : fb ? fb.taxRate : 0;
  const subtotalHT = hasHT ? round2(tr?.subtotalHT) : round2(fb?.subtotalHT ?? totals?.subtotal ?? 0);
  const taxAmount = hasTax ? round2(tr?.taxAmount) : round2(fb?.taxAmount ?? totals?.taxAmount ?? 0);
  const rateFmt = taxRate % 1 === 0 ? taxRate.toFixed(0) : taxRate.toFixed(1);

  const rawMode = data.taxSettings?.displayMode;
  const flags = getTaxDisplayFlags(rawMode === 'hidden' ? 'compact' : rawMode || 'detailed');

  sep();
  if (flags.showSubtotalHT) priced(S(tk('totals.subtotalHT')), money(subtotalHT));
  if (flags.showTaxAmount) {
    if (rateLines.length) {
      for (const r of rateLines) {
        const rf = Number(r.rate) % 1 === 0 ? Number(r.rate).toFixed(0) : Number(r.rate).toFixed(1);
        priced(S(tk('totals.taxWithRate', { rate: rf })), money(r.amount));
      }
    } else {
      priced(
        flags.showTaxRate ? S(tk('totals.taxWithRate', { rate: rateFmt })) : S(tk('totals.tax')),
        money(taxAmount)
      );
    }
  }

  const deliveryFee = round2(data.deliveryFee || 0);
  if (deliveryFee > 0) priced(S(tk('totals.deliveryFeeShort')), money(deliveryFee));

  const discount = round2(data.discount || 0);
  if (discount > 0) priced(S(tk('totals.discount')), `-${money(discount)}`);

  // Remises NOMMÉES de la borne (HAPPY HOUR) — montant et libellé OFFICIELS du serveur.
  // ⚠️ NF525 : un prix différent du catalogue doit être justifié sur le ticket.
  for (const line of Array.isArray(data.extraDiscountLines) ? data.extraDiscountLines : []) {
    const amount = round2(line?.amount);
    if (amount > 0) priced(S(line.label), `-${money(amount)}`);
  }

  if (loyaltyDiscount > 0) {
    const label = loyaltyPoints
      ? S(tk('totals.loyaltyDiscountPoints', { points: loyaltyPoints }))
      : S(tk('totals.loyaltyDiscount'));
    priced(label.replace(/:$/, ''), `-${money(loyaltyDiscount)}`);
  }

  // TOTAL : `<b>` + MAJUSCULES, jamais `<font size='big'>` (R9 : la longueur serait
  // multipliée par 2 à 7 et la ligne déborderait). En prime, `size='big'` n'est de toute
  // façon JAMAIS appliqué sur Star (BT teste `SIZE='BIG'` en majuscules, USB supprime la
  // balise) : le `<b>` est le seul rendu réellement commun au parc.
  priced(S(tk('totals.totalTTCUpper')), `${money(totalTTC)} ${cur}`, true);
  sep();

  /* ── 5. PAIEMENT ──────────────────────────────────────────────────────────── */
  const payLines = Array.isArray(data.paymentLines) ? data.paymentLines : [];
  if (data.isPaymentPending) {
    cen(S(tk('payment.toCollect')), { b: true });
  } else if (payLines.length > 1) {
    left(`${S(tk('payment.label'))} : ${S(tk('payment.mixed'))}`, true);
    for (const p of payLines) priced(`  ${S(p.label)}`, money(p.amount));
  } else if (payLines.length === 1) {
    priced(S(payLines[0].label), money(payLines[0].amount));
  } else {
    const label =
      S(data.paymentModeLabel) ||
      (data.paymentMethod ? S(tkPayment(data.paymentMethod)) : S(tk('payment.notSpecified')));
    left(`${S(tk('payment.label'))} : ${label}`);
  }

  /* ── 6. NOTES de commande ─────────────────────────────────────────────────── */
  const notes: Array<[string, string]> = [];
  if (data.customerNote) notes.push([S(tk('items.customerNoteUpper')), S(data.customerNote)]);
  if (data.delivery_note) notes.push([S(tk('notes.delivery')), S(data.delivery_note)]);
  if (data.pickup_note) notes.push([S(tk('notes.pickup')), S(data.pickup_note)]);
  if (notes.length) {
    blank();
    for (const [label, text] of notes) {
      // « LABEL : texte » — l'espace avant le deux-points désamorce la détection de
      // commentaire côté natif (R5) ; `guardComment` couvre le reste.
      for (const part of wrap(`${label} : ${text}`, w)) left(guardComment(part));
    }
  }

  /* ── 7. PIED ──────────────────────────────────────────────────────────────── */
  blank();
  cen(S(tk('footer.thankYou')));
  if (data.profileSettings?.siretMessage) cen(S(data.profileSettings.siretMessage));
  if (data.profileSettings?.tvaMessage) cen(S(data.profileSettings.tvaMessage));
  // R7 : PAS de lignes d'avance ni de coupe ici — le natif ajoute déjà 20 mm (ESC/POS)
  // ou 5 sauts de ligne (Star) puis coupe INCONDITIONNELLEMENT.

  /* ═══════════════════════════════════════════════════════════════════════════
   * RENDU
   * ═══════════════════════════════════════════════════════════════════════════ */
  const bold = (t: string, on?: boolean) => (on && markup === 'escpos' ? `<b>${t}</b>` : t);
  const out: string[] = [];

  for (const ln of L) {
    switch (ln.k) {
      case 'blank':
        out.push('');
        break;
      case 'sep':
        // `=` et non `-` : 24 tirets consécutifs arment le piège du pré-traitement natif
        // (lignes identiques avalées + séparateur parasite en fin de ticket) — R6.
        out.push(markup === 'escpos' ? `[C]${'='.repeat(w)}` : '='.repeat(w));
        break;
      case 'c': {
        const t = ln.t.slice(0, w);
        out.push(markup === 'escpos' ? `[C]${bold(t, ln.b)}` : padCenter(t, w));
        break;
      }
      case 'l': {
        const t = ln.t.slice(0, w);
        out.push(markup === 'escpos' ? `[L]${bold(t, ln.b)}` : t);
        break;
      }
      case 'pair': {
        const t = padPair(ln.left, ln.right, w);
        out.push(markup === 'escpos' ? `[L]${bold(t, ln.b)}` : t);
        break;
      }
      case 'raw':
        // Balise de routage SEULE sur sa ligne, sans préfixe d'alignement ni `<b>` :
        // la regex de `printQueue` la cherche telle quelle et `stripMarkers()` la retire
        // du ticket client par la même écriture exacte.
        out.push(ln.t);
        break;
    }
  }

  // R13 : au plus une ligne vide consécutive ; R7 : pas d'avance finale ; R1 : `\n` seul.
  // R13 : au plus une ligne vide consécutive. R7 : aucune ligne d'avance finale — mais
  // on retire les lignes VIDES, pas les espaces de tête (un `.trim()` global décentrerait
  // la première ligne en mode `plain`, où le centrage est fait d'espaces).
  const rendered = out.map(guardArticles);
  while (rendered.length && rendered[0].trim() === '') rendered.shift();
  while (rendered.length && rendered[rendered.length - 1].trim() === '') rendered.pop();
  return rendered.join('\n').replace(/\n{3,}/g, '\n\n');
}

/**
 * Adaptateur pour le dispatcher `buildTicket()` : traduit la configuration du profil en
 * options du module. La largeur vient DU PROFIL (jamais d'une constante en dur) parce
 * que le réglage réel de l'imprimante n'est pas lisible depuis la console.
 */
export const buildUnifiedTicketFromProfile = (data: TicketData, profile?: PrinterProfile): string =>
  buildUnifiedTicket(data, {
    width: profile?.config?.width,
    markup: profile?.config?.plainText === true ? 'plain' : 'escpos',
    asciiOnly: profile?.config?.asciiOnly === true,
    emitCategoryMarkers: profile?.config?.showCategories !== false,
  });
